import Translations from "@translate/utils/member/subscriptionErrors";
import { I18nUtil } from "@utils/i18n";

const I18n = I18nUtil.createScoped(Translations);

/**
 * 订阅管理接口（PATCH / 换卡 / 升级）的错误码（EDG-552，fe-spec §2.2 v1.9）。
 * mixture 透传码，`buildCode(n) = 14 × 10000 + n`；网关不再翻译，HTTP 状态由 mixture 声明。
 */
export const SUBSCRIPTION_MANAGE_CODE = {
  // 换档缺少或版本不被接受的 consent（后端 D-12）：前端已按 CONSENT_VERSION 传参仍被拒 = 后端版本未先发
  CONSENT_REQUIRED: 146205,
  PAYMENT_PLAN_NOT_OWNED: 146088,
  SUBSCRIPTION_CAPABILITY_UNSUPPORTED: 146089,
  PAYMENT_METHOD_SESSION_NOT_FOUND: 146206,
  PLAN_CHANGE_NOT_ALLOWED: 146207,
} as const;

type SubscriptionManageCodeName = keyof typeof SUBSCRIPTION_MANAGE_CODE;

export interface SubscriptionManageError {
  message: string;
  /** 本地 plans / 商品已过期，调用方应刷新后再让用户重试 */
  shouldRefetch: boolean;
}

const GENERIC_ERROR = (): SubscriptionManageError => ({
  message: I18n.t("Couldn't update. Please try again."),
  shouldRefetch: false,
});

const NOT_AVAILABLE_MESSAGE = (): string =>
  I18n.t("This action isn't available for this subscription.");

const ERROR_BY_NAME: Record<
  SubscriptionManageCodeName,
  () => SubscriptionManageError
> = {
  // 与下单 146205 同一文案（`getPaymentCheckErrorMessage`）；不刷新 —— 刷新解决不了发布顺序问题
  CONSENT_REQUIRED: () => ({
    message: I18n.t("Please review the terms and try again."),
    shouldRefetch: false,
  }),
  PAYMENT_PLAN_NOT_OWNED: () => ({ ...GENERIC_ERROR(), shouldRefetch: true }),
  SUBSCRIPTION_CAPABILITY_UNSUPPORTED: () => ({
    message: NOT_AVAILABLE_MESSAGE(),
    shouldRefetch: true,
  }),
  PAYMENT_METHOD_SESSION_NOT_FOUND: () => ({
    message: I18n.t("Session expired. Please try again."),
    shouldRefetch: false,
  }),
  // fe-spec §2.2 只规定「Toast + 刷新商品与 plans」未给文案，复用能力不支持的文案（同一语义：此操作当前不可用）
  PLAN_CHANGE_NOT_ALLOWED: () => ({
    message: NOT_AVAILABLE_MESSAGE(),
    shouldRefetch: true,
  }),
};

const isCodeName = (value?: string): value is SubscriptionManageCodeName =>
  value != null && Object.hasOwn(SUBSCRIPTION_MANAGE_CODE, value);

function resolveCodeName(
  code?: number | string,
  msg?: string,
): SubscriptionManageCodeName | undefined {
  const numeric = typeof code === "string" ? Number(code) : code;
  const byCode = (
    Object.keys(SUBSCRIPTION_MANAGE_CODE) as SubscriptionManageCodeName[]
  ).find((name) => SUBSCRIPTION_MANAGE_CODE[name] === numeric);
  return byCode ?? (isCodeName(msg) ? msg : undefined);
}

/**
 * 管理接口错误 → 用户提示。code 优先，其次按 msg 名称匹配（与 `getPaymentCheckErrorMessage` 同口径）；
 * 网络错误 / 5xx / 未知码一律通用文案，UI 保持操作前状态（不做乐观更新）。
 */
export function getSubscriptionManageError(res?: {
  code?: number | string;
  msg?: string;
}): SubscriptionManageError {
  const name = resolveCodeName(res?.code, res?.msg);
  return name ? ERROR_BY_NAME[name]() : GENERIC_ERROR();
}
